/**
 * Types pour le système de suivi d'activité
 */

/**
 * Type pour les noms d'activités
 */
export type ActivityName =
  // Markers
  | 'Marker.Created'
  | 'Marker.Updated'
  | 'Marker.Moved'
  | 'Marker.Deleted'
  // Notifications
  | 'Notification.Created'
  | 'Notification.Scheduled'
  | 'Notification.Sent'
  | 'Notification.Checked'
  | 'Notification.Cancelled'
  | 'Notification.Failed'
  // Contacts
  | 'Contact.Created'
  | 'Contact.Updated'
  | 'Contact.Deleted'
  // Actions
  | 'Action.Created'
  | 'Action.Updated'
  | 'Action.Completed'
  | 'Action.Deleted';

/**
 * Type pour les types de cibles
 */
export type ActivityTargetType = 'marker' | 'notification' | 'contact' | 'action';

/**
 * Type pour les résultats d'activités
 */
export type ActivityResult = 'success' | 'failure';

/**
 * Type pour les périodes de filtrage
 */
export type ActivityPeriod = 'today' | '3days' | '1week' | '1month' | 'all';

/**
 * Labels d'affichage pour les périodes
 */
export const ACTIVITY_PERIOD_LABELS: Record<ActivityPeriod, string> = {
  today: "Aujourd'hui",
  '3days': '3 derniers jours',
  '1week': '7 derniers jours',
  '1month': '30 derniers jours',
  all: 'Tout'
};

/**
 * Nombre de jours correspondant à chaque période
 */
export const ACTIVITY_PERIOD_DAYS: Record<ActivityPeriod, number | null> = {
  today: 1,
  '3days': 3,
  '1week': 7,
  '1month': 30,
  all: null
};

/**
 * Emojis pour les types d'activités
 */
export const ACTIVITY_EMOJIS: Record<ActivityName, string> = {
  // Markers
  'Marker.Created': '🏷️',
  'Marker.Updated': '🏷️',
  'Marker.Moved': '📍',
  'Marker.Deleted': '🗑️',
  // Notifications
  'Notification.Created': '🔔',
  'Notification.Scheduled': '⏰',
  'Notification.Sent': '📤',
  'Notification.Checked': '✅',
  'Notification.Cancelled': '❌',
  'Notification.Failed': '⚠️',
  // Contacts
  'Contact.Created': '👤',
  'Contact.Updated': '👤',
  'Contact.Deleted': '🗑️',
  // Actions
  'Action.Created': '📋',
  'Action.Updated': '📝',
  'Action.Completed': '✅',
  'Action.Deleted': '🗑️'
};

/**
 * Labels d'affichage pour les types d'activités
 */
export const ACTIVITY_LABELS: Record<ActivityName, string> = {
  // Markers
  'Marker.Created': 'Marker créé',
  'Marker.Updated': 'Marker modifié',
  'Marker.Moved': 'Marker déplacé',
  'Marker.Deleted': 'Marker supprimé',
  // Notifications
  'Notification.Created': 'Notification créée',
  'Notification.Scheduled': 'Notification planifiée',
  'Notification.Sent': 'Notification envoyée',
  'Notification.Checked': 'Notification traitée',
  'Notification.Cancelled': 'Notification annulée',
  'Notification.Failed': 'Échec notification',
  // Contacts
  'Contact.Created': 'Contact créé',
  'Contact.Updated': 'Contact modifié',
  'Contact.Deleted': 'Contact supprimé',
  // Actions
  'Action.Created': 'Action créée',
  'Action.Updated': 'Action modifiée',
  'Action.Completed': 'Action terminée',
  'Action.Deleted': 'Action supprimée'
};

/**
 * Interface représentant une activité
 */
export interface Activity {
  id: string;
  occurredAt: Date;
  agencyId: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  activityName: ActivityName;
  targetType: ActivityTargetType;
  targetId: string;
  result: ActivityResult;
  notes?: string;
  geoLat?: number;
  geoLng?: number;
  createdAt: Date;
}

/**
 * Interface pour les données brutes de Supabase
 */
export interface RawActivity {
  id: string;
  occurred_at: string;
  agency_id: string;
  user_id: string;
  user_email?: string;
  user_name?: string;
  activity_name: ActivityName;
  target_type: ActivityTargetType;
  target_id: string;
  result: ActivityResult;
  notes?: string;
  geo_lat?: number;
  geo_lng?: number;
  created_at: string;
}

/**
 * Interface pour les filtres d'activités
 */
export interface ActivityFilters {
  agencyId: string;
  userId?: string; // undefined = toutes les activités de l'agence (pour admin)
  period?: ActivityPeriod;
  limit?: number;
  offset?: number;
}

/**
 * Interface pour la réponse paginée
 */
export interface ActivityResponse {
  activities: Activity[];
  total: number;
  hasMore: boolean;
}
